import './utils/buttons.css';
import { isContractDisabled } from './utils/contractStatus';
import { useState, useEffect, Fragment, useRef } from "react";
import { useSignal } from "@preact/signals-react";
import ConfirmationModal from "../../templates/ConfirmationModal";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Auth } from "aws-amplify";
import TextEdit from "../../molecules/TextEdit";
import TextArea from "../../molecules/TextArea";
import SelectTextField from "../../molecules/SelectTextField";
import CurrencyInput from "../../molecules/CurrencyInput";
import { applyDateMask } from '../../../utils/dateMask';
import PageForm from "../../templates/PageForm";
import useProvinces from "../../../hooks/useProvinces";
import useCities from "../../../hooks/useCities";
import { FaLeaf, FaMapLocationDot, FaMap } from "react-icons/fa6";
import { FaEllipsisV, FaSave, FaBan } from "react-icons/fa";
import ParticipantsManager from './components/ParticipantsManager';
import FeesManager from './components/FeesManager';
import InstallmentManager from './components/InstallmentManager';
import GuarantorsManager from './components/GuarantorsManager';
import DisbursementLoanManager from './components/DisbursementLoanManager';
import { CropType, Participant, CPRFFormData } from './types/operations';
import { ContractApiResponse } from './types/api-responses';
import appConfig from '../../../config/app-config';

const { address: CONTRACTS_API_URL, key: CONTRACTS_API_KEY } = appConfig.apis.contractsManagementApiEnv;

const BACKOFFICE_URL = appConfig.apis.backofficeApiUrl;
const CONTRACTS_LIST_URL = '/contracts/0/100/id/ASC/s=;';

const determineEntityType = (document: string, entityType?: string): string => {
    // Se já tem entity_type definido, usa ele
    if (entityType && (entityType === 'PJ' || entityType === 'PF')) {
        return entityType;
    }

    // Remove caracteres não numéricos
    const cleanDoc = document.replace(/\D/g, '');

    // CPF tem 11 dígitos, CNPJ tem 14
    if (cleanDoc.length === 14) {
        return 'PJ';
    } else if (cleanDoc.length === 11) {
        return 'PF';
    }

    return '';
};

const loadGuarantorsFromParticipants = (participants?: any[]) => {
    if (!participants || participants.length === 0) {
        return [];
    }

    const guarantorParticipants = participants.filter(p =>
        p.participant_type_name === 'GUARANTOR' || p.participant_type_name === 'GUARANTOR-RETAIL'
    );

    return guarantorParticipants.map((p: any) => {
        const entityType = p.participant_type_name === 'GUARANTOR-RETAIL'
            ? determineEntityType(p.document || '', p.entity_type)
            : (p.entity_type || '');

        return {
            uuid: p.person_id || '',
            name: p.name || '',
            document: p.document || '',
            entity_type: entityType,
            guarantee_percentage: p.guarantee_percentage || 0,
            participant_type_name: p.participant_type_name || ''
        };
    });
};

const OperationLoanFormPage = () => {
    const navigate = useNavigate();
    const params = useParams();
    const credit_operation_id = params.credit_operation_id || params.id;
    const { fetchProvinces } = useProvinces();
    const { fetchCities } = useCities();
    const [isLoading, setIsLoading] = useState(false);
    const [cities, setCities] = useState<Array<{ value: string, label: string }>>([]);
    const [provinces, setProvinces] = useState<Array<{ value: string, label: string, initials: string }>>([]);
    const [cropTypes, setCropTypes] = useState<Array<{ value: string, label: string }>>([]);
    const [selectedParticipants, setSelectedParticipants] = useState<Participant[]>([]);
    const [CONTRACT_TEMPLATE_ID, setCONTRACT_TEMPLATE_ID] = useState<number | null>(null);
    const [disbursements, setDisbursements] = useState<any[]>([]);
    let [selectCropType, setSelectCropType] = useState<CropType>();
    let [cropTypeId, setCropTypeId] = useState(0);

    const getCurrentDate = (): string => {
        const today = new Date();
        const day = String(today.getDate()).padStart(2, '0');
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const year = today.getFullYear();
        return `${day}/${month}/${year}`;
    };

    const formatHarvestYear = (value: string): string => {
        const numbers = value.replace(/\D/g, '');
        if (numbers.length <= 2) return numbers;
        return `${numbers.slice(0, 2)}/${numbers.slice(2, 4)}`;
    };

    const handleHarvestYearChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const formatted = formatHarvestYear(e.target.value);
        setFormData({
            ...formData,
            operation_dictionary: {
                ...formData.operation_dictionary,
                commodity: { ...formData.operation_dictionary.commodity, harvest_year_reference: formatted }
            }
        });
    };

    const [contractStatus, setContractStatus] = useState<string>('');
    const isFormDisabled = isContractDisabled(contractStatus);
    const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const apiVersion = useSignal('');

    const handleCancelContract = async () => {
        try {
            setIsLoading(true);
            const session = await Auth.currentSession();
            const token = session.getIdToken().getJwtToken();
            const response = await fetch(`${CONTRACTS_API_URL}/contracts/${credit_operation_id}/status/CANCELED`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'x-api-key': CONTRACTS_API_KEY || ''
                }
            });
            if (response.ok) {
                toast.success('Contrato cancelado com sucesso!');
                navigate('/contracts/0/100/id/ASC/s=;');
            } else {
                toast.error('Erro ao cancelar contrato');
            }
        } catch (error) {
            toast.error('Erro ao conectar com o servidor');
        } finally {
            setIsLoading(false);
            setIsCancelModalOpen(false);
        }
    };
    const [formData, setFormData] = useState<CPRFFormData>({
        type: 'CPRF',
        operation_number: '',
        sequencial_number: 0,
        nominal_value: 0,
        loan_amount: 0,
        approved_value: '',
        interest_rate: '',
        expiration_date: '',
        external_link: '',
        operation_uf: '',
        operation_city: '',
        operation_dictionary: {
            type: 'CPRF',
            nominal_value: 0,
            requested_value: 0,
            interest_rate: 0,
            emission_date: getCurrentDate(),
            status: 1,
            guarantee_value: null,
            measures: {
                aggregation_unit_measure: '',
                amount_in_aggregation_unit: 0,
                referency: 0,
                unit_measure: ''
            },
            commodity: {
                harvest_year_reference: '',
                price: 0,
                quality: '',
                description: '',
                amount: 0,
                quotation: {
                    date: getCurrentDate(),
                    source: '',
                    city: '',
                    province_name: '',
                    province_initials: ''
                }
            },
            crop_type: {
                id: 0,
                name: ''
            },
            guarantors: [],
            installment: {
                total: 0,
                lastDueDate: '',
                quantity: 0,
                items: []
            },
            fees: {
                quantity: 0,
                items: []
            }
        }
    });

    const onQuotationValueChange = (value: number) => {
        const commodityBagAmount = formData.nominal_value / value;
        const commodityAmount = commodityBagAmount * formData.operation_dictionary.measures.referency;
        setFormData({
            ...formData, operation_dictionary: {
                ...formData.operation_dictionary,
                measures: {
                    ...formData.operation_dictionary.measures,
                    amount_in_aggregation_unit: commodityBagAmount,
                },
                commodity: {
                    ...formData.operation_dictionary.commodity,
                    amount: commodityAmount
                }
            }
        });
        // formData.operation_dictionary.measures.amount_in_aggregation_unit = commodityAmount;
    }

    const fetchContractData = async () => {
        if (!credit_operation_id || !CONTRACTS_API_URL || !CONTRACTS_API_KEY) {
            return null;
        }

        try {
            setIsLoading(true);
            const token = await Auth.currentSession()
                .then((user) => user.getIdToken().getJwtToken());

            const response = await fetch(`${CONTRACTS_API_URL}/operations/loan/${credit_operation_id}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'x-api-key': CONTRACTS_API_KEY || ''
                }
            });

            if (response.ok) {
                const data: ContractApiResponse = await response.json();
                const schema = data.operation_dictionary || {};
                setContractStatus(data.contract_built_status || '');

                // Set API version info if available
                if (data.apiName && data.apiVersion) {
                    apiVersion.value = `${data.apiName} - ${data.apiVersion}`;
                }

                // Format date from ISO to DD/MM/YYYY
                const formatDate = (isoDate: string) => {
                    if (!isoDate) return getCurrentDate();
                    const dateOnly = isoDate.split('T')[0];
                    const [year, month, day] = dateOnly.split('-');
                    return `${day}/${month}/${year}`;
                };

                const newFormData = {
                    type: schema.type || 'CPRF',
                    operation_number: data.operation_number || '',
                    sequencial_number: data.sequencial_number || 0,
                    nominal_value: data.nominal_value || 0,
                    loan_amount: data.loan_amount || 0,
                    approved_value: data.approved_value?.toString() || '',
                    interest_rate: '',
                    expiration_date: data.expiration_date ? formatDate(data.expiration_date) : '',
                    external_link: schema.external_link || '',
                    operation_uf: schema.operation_uf || '',
                    operation_city: schema.operation_city || '',
                    operation_dictionary: {
                        type: schema.type || 'CPRF',
                        nominal_value: schema.nominal_value || 0,
                        requested_value: schema.requested_value || 0,
                        interest_rate: schema.interest_rate || 0,
                        emission_date: schema.emission_date ? formatDate(schema.emission_date) : getCurrentDate(),
                        status: schema.status || 1,
                        guarantee_value: schema.guarantee_value || null,
                        measures: {
                            referency: schema.measures?.referency || 0,
                            aggregation_unit_measure: schema.measures?.aggregation_unit_measure || '',
                            amount_in_aggregation_unit: schema.measures?.amount_in_aggregation_unit || 0,
                            unit_measure: schema.measures?.unit_measure || ''
                        },
                        commodity: {
                            harvest_year_reference: schema.commodity?.harvest_year_reference || '',
                            price: schema.commodity?.price || 0,
                            quality: schema.commodity?.quality || '',
                            description: schema.commodity?.description || '',
                            amount: schema.commodity?.amount || 0,
                            quotation: {
                                date: schema.commodity?.quotation?.date ? formatDate(schema.commodity?.quotation?.date) : getCurrentDate(),
                                source: schema.commodity?.quotation?.source || '',
                                city: schema.commodity?.quotation?.city || '',
                                province_name: schema.commodity?.quotation?.province_name || '',
                                province_initials: schema.commodity?.quotation?.province_initials || ''
                            }
                        },
                        crop_type: {
                            id: schema.crop_type?.id || 0,
                            name: schema.crop_type?.name || ''
                        },
                        main_risk: schema.main_risk || undefined,
                        guarantors: (Array.isArray(schema.guarantors) ? schema.guarantors : schema.guarantors?.items || []).map((g: any) => ({
                            uuid: g.uuid || g.person_id,
                            name: g.name,
                            document: g.document,
                            entity_type: g.entity_type,
                            guarantee_percentage: g.guarantee_percentage || 0,
                            participant_type_name: g.participant_type || g.participant_type_name
                        })),
                        installment: schema.installment || {
                            total: 0,
                            lastDueDate: '',
                            quantity: 0,
                            items: []
                        },
                        fees: schema.fees || {
                            quantity: 0,
                            items: []
                        }
                    }
                };

                setFormData(newFormData as CPRFFormData);

                console.log('📊 Dados carregados:', {
                    main_risk: schema.main_risk,
                    guarantors: schema.guarantors,
                    formData_main_risk: newFormData.operation_dictionary.main_risk,
                    formData_guarantors: newFormData.operation_dictionary.guarantors
                });

                // Load disbursements from operation_dictionary
                if (schema.disbursements?.items && Array.isArray(schema.disbursements.items)) {
                    setDisbursements(schema.disbursements.items);
                }

                // Set crop type ID for dependent fields
                if (schema.crop_type?.id) {
                    setCropTypeId(schema.crop_type.id);
                }

                // Load participants
                if (data.participants && data.participants.length > 0) {
                    const participants = data.participants.map((p: any) => ({
                        id: p.id?.toString() || Date.now().toString(),
                        person_id: p.person_id || '',
                        participant_type: (p.participant_type_name || p.participant_type) as 'BORROWER' | 'GUARANTOR' | 'ASSIGNOR' | 'GUARANTOR-RETAIL' | 'RECIPIENT',
                        name: p.name || 'Nome não informado',
                        document: p.document || ''
                    }));
                    setSelectedParticipants(participants);
                }

                return { schema, data };
            } else {
                toast.error('Erro ao carregar dados da operação');
                return null;
            }
        } catch (error) {
            toast.error('Erro ao conectar com o servidor');
            return null;
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        const fetchProvincesData = async () => {
            const provincesData = await fetchProvinces();
            const provinceItems = provincesData.map((item: any) => ({
                value: item.id,
                label: item.name,
                initials: item.initials
            }));
            setProvinces(provinceItems);
            return provinceItems;
        };

        const loadDataSequentially = async () => {
            const provincesData = await fetchProvincesData();

            if (credit_operation_id) {
                const contractData = await fetchContractData();
                
                // Após carregar os dados do contrato, buscar cidades se UF estiver selecionada
                if (contractData?.schema?.operation_uf) {
                    const selectedProvince = provincesData.find((p: { value: string; label: string; initials: string }) => p.initials === contractData.schema.operation_uf);
                    if (selectedProvince) {
                        try {
                            await fetchCitiesWrapper(selectedProvince.initials);
                        } catch (error) {
                            console.error('Erro ao carregar cidades:', error);
                        }
                    }
                }
                
                // Load cities for quotation
                if (contractData?.schema?.commodity?.quotation?.province_initials) {
                    await fetchCitiesWrapper(contractData.schema.commodity.quotation.province_initials);
                }
            }
        };

        const fetchData = async () => {
            try {
                // Buscar tipos de cultura
                const cropTypesResponse = await fetch(`${BACKOFFICE_URL}/services/croptypes/all`);
                if (cropTypesResponse.ok) {
                    const cropTypesData = await cropTypesResponse.json();
                    const cropTypeItems = cropTypesData.map((item: any) => ({
                        value: item.id.toString(),
                        label: item.name
                    }));
                    setCropTypes(cropTypeItems);
                }

                // Buscar template de contrato
                const templateResponse = await fetch(`${BACKOFFICE_URL}/services/contract-template/loan`);
                if (templateResponse.ok) {
                    const templateData = await templateResponse.json();
                    setCONTRACT_TEMPLATE_ID(templateData.id);
                }
            } catch (error) {
            }
        };
        
        fetchData();
        loadDataSequentially();
    }, [credit_operation_id]);

    const handleProvinceChange = async (initials: string) => {
        if (initials) {
            const selectedProvince = provinces.find(p => p.initials === initials);
            if (selectedProvince) {
                const citiesData = await fetchCities(selectedProvince.value);
                setCities(citiesData);
            }
            setFormData({
                ...formData,
                operation_uf: initials,
                operation_city: ''
            });
        } else {
            setCities([]);
            setFormData({
                ...formData,
                operation_uf: '',
                operation_city: ''
            });
        }
    };

    const validateForm = (): boolean => {
        if (!formData.nominal_value) {
            toast.error('Valor Nominal é obrigatório');
            return false;
        }
        if (selectedParticipants.length === 0) {
            toast.error('Pelo menos um participante é obrigatório');
            return false;
        }
        if (!formData.operation_dictionary.commodity.harvest_year_reference) {
            toast.error('Ano de Safra é obrigatório');
            return false;
        }
        if (!formData.operation_dictionary.commodity.price) {
            toast.error('Preço é obrigatório');
            return false;
        }
        if (!formData.operation_dictionary.measures.amount_in_aggregation_unit) {
            toast.error('Quantidade é obrigatória');
            return false;
        }
        if (!formData.operation_dictionary.commodity.quotation.date) {
            toast.error('Data da Cotação é obrigatória');
            return false;
        }
        if (!formData.operation_dictionary.commodity.quotation.source) {
            toast.error('Fonte da Cotação é obrigatória');
            return false;
        }
        if (!formData.operation_dictionary.commodity.quotation.city) {
            toast.error('Cidade é obrigatória');
            return false;
        }
        if (!formData.operation_dictionary.commodity.quotation.province_name) {
            toast.error('Província é obrigatória');
            return false;
        }
        if (!formData.operation_dictionary.crop_type.id) {
            toast.error('Tipo de Cultura é obrigatório');
            return false;
        }
        return true;
    };

    const fetchSelectedCropType = async () => {
        try {
            const response = await fetch(`${BACKOFFICE_URL}/services/croptypes/${cropTypeId}`);
            if (!response.ok) {
                throw new Error(`Erro na requisição: ${response.statusText}`);
            }
            const data: CropType = await response.json();
            setSelectCropType(data);
            setFormData(prev => ({
                ...prev,
                operation_dictionary: {
                    ...prev.operation_dictionary,
                    measures: {
                        ...prev.operation_dictionary.measures,
                        aggregation_unit_measure: data.aggregationUnitMeasureDTO?.plural || '',
                        unit_measure: data.commodityUnitMeasure || ''
                    },
                    commodity: {
                        ...prev.operation_dictionary.commodity,
                        description: data.description || '',
                        quality: data.qualityStandard || ''
                    }
                }
            }));
        } catch (err) {
        } finally {
        }
    };

    useEffect(() => {
        if (cropTypeId > 0) {
            fetchSelectedCropType();
        }
    }, [cropTypeId]);

    useEffect(() => {
        // Execute calculation after form data is loaded
        if (formData.operation_dictionary.commodity.price > 0 && formData.nominal_value > 0) {
            onQuotationValueChange(formData.operation_dictionary.commodity.price);
        }
    }, [formData.operation_dictionary.commodity.price, formData.nominal_value]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const convertDateToISO = (dateStr: string): string => {
        if (!dateStr) return '';
        // Se já está no formato ISO (YYYY-MM-DD), retorna como está
        if (/^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
            return dateStr.split('T')[0];
        }
        // Converte DD/MM/YYYY para YYYY-MM-DD
        if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) {
            const [day, month, year] = dateStr.split('/');
            return `${year}-${month}-${day}`;
        }
        return dateStr;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;

        if (!CONTRACTS_API_URL || !CONTRACTS_API_KEY) {
            toast.error('Configuração da API não encontrada');
            return;
        }

        setIsLoading(true);

        try {
            const token = await Auth.currentSession()
                .then((user) => user.getIdToken().getJwtToken());

            const payload = {
                type: formData.type,
                operation_type: 'LOAN',
                operation_number: formData.operation_number,
                sequencial_number: formData.sequencial_number,
                operation_schema_id: 1,
                approved_value: parseFloat(formData.approved_value) || 0,
                interest_rate: formData.interest_rate,
                expiration_date: formData.expiration_date,
                due_date: formData.expiration_date,
                nominal_value: formData.nominal_value,
                loan_amount: formData.loan_amount || 0,

                contract_built_status: 1,
                register_status: null,
                contract_template_id: CONTRACT_TEMPLATE_ID,

                participants: selectedParticipants.map(participant => ({
                    person_id: participant.person_id,
                    participant_type: participant.participant_type,
                    is_primary: true,
                    name: participant.name,
                    document: participant.document,
                })),
                operation_dictionary: {
                    ...formData.operation_dictionary,
                    external_link: formData.external_link || '',
                    operation_uf: formData.operation_uf,
                    operation_city: formData.operation_city
                }
            };

            const url = credit_operation_id
                ? `${CONTRACTS_API_URL}/contracts/loan/save`
                : `${CONTRACTS_API_URL}/contracts/loan/save`;

            const payload_with_id = credit_operation_id
                ? { ...payload, credit_operation_id }
                : payload;

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                    'x-api-key': CONTRACTS_API_KEY
                },
                body: JSON.stringify(payload_with_id)
            });

            if (response.ok) {
                const message = credit_operation_id
                    ? 'Operação de empréstimo CPRF atualizada com sucesso!'
                    : 'Operação de empréstimo CPRF criada com sucesso!';
                toast.success(message);
            } else {
                const errorData = await response.json();
                toast.error(errorData.message || 'Erro ao salvar operação');
            }
        } catch (error) {
            toast.error('Erro ao conectar com o servidor');
        } finally {
            setIsLoading(false);
        }
    };

    const participantTypes = [
        { value: 'BORROWER', label: 'Tomador do crédito' },
        { value: 'GUARANTOR', label: 'Avalista/Fiador' },
        { value: 'GUARANTOR-RETAIL', label: 'Avalista/Fiador revenda parceira' },
        { value: 'ASSIGNOR', label: 'Cedente' }
    ];

    const handleQuotationProvinceChange = async (provinceId: string) => {
        if (provinceId) {
            const selectedProvince = provinces.find(p => p.value === provinceId) || null;
            if (selectedProvince) {
                await fetchCitiesWrapper(selectedProvince.initials);
            }
            setFormData({
                ...formData,
                operation_dictionary: {
                    ...formData.operation_dictionary,
                    commodity: {
                        ...formData.operation_dictionary.commodity,
                        quotation: {
                            ...formData.operation_dictionary.commodity.quotation,
                            city: '',
                            province_name: selectedProvince?.label || '',
                            province_initials: selectedProvince?.initials || ''
                        }
                    }
                }
            });
        } else {
            setCities([]);
            setFormData({
                ...formData,
                operation_dictionary: {
                    ...formData.operation_dictionary,
                    commodity: {
                        ...formData.operation_dictionary.commodity,
                        quotation: {
                            ...formData.operation_dictionary.commodity.quotation,
                            city: '',
                            province_name: '',
                            province_initials: ''
                        }
                    }
                }
            });
        }
    };

    const fetchCitiesWrapper = async (initials: string) => {
        try {
            setIsLoading(true);
            if (initials) {
                const citiesData = await fetchCities(initials);
                const cityItems = citiesData.map((item: any) => ({
                    value: item.name,
                    label: item.name
                }));
                setCities(cityItems);

            }
        } catch (error) {
            toast.error('Erro ao carregar cidades');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Fragment>
            <PageForm
                pageTitle="Nova Operação de Empréstimo CPRF"
                apiVersion={apiVersion.value}
                isLoading={isLoading}
                breadcrumb={[
                    { label: 'Contratos', url: CONTRACTS_LIST_URL },
                    { label: 'Nova operação de empréstimo CPRF', url: '#' }
                ]}
                controlSlot={(
                    <div className="pt-4 flex justify-end">
                        <button
                            className="btn-cancel ml-2"
                            onClick={() => navigate(-1)}
                            disabled={isLoading}
                        >
                            Voltar
                        </button>

                        {/* Dropdown de Ações */}
                        <div ref={dropdownRef} className="relative inline-block text-left ml-2">
                            <button
                                type="button"
                                className="inline-flex justify-center w-full rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 dark:bg-slate-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-slate-700"
                                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                                disabled={isLoading}
                            >
                                Ações
                                <FaEllipsisV className="ml-2 h-4 w-4" />
                            </button>

                            {isDropdownOpen && (
                                <div className="origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-50 dark:bg-slate-800">
                                    <div className="py-1">
                                        <button
                                            className="group flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 w-full text-left disabled:opacity-50 disabled:cursor-not-allowed dark:text-gray-300 dark:hover:bg-slate-700"
                                            onClick={() => {
                                                handleSubmit();
                                                setIsDropdownOpen(false);
                                            }}
                                            disabled={isLoading || isFormDisabled}
                                        >
                                            <FaSave className="mr-3 h-4 w-4 text-green-500" />
                                            Salvar
                                        </button>
                                        <button
                                            className="group flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 w-full text-left disabled:opacity-50 disabled:cursor-not-allowed dark:text-gray-300 dark:hover:bg-slate-700"
                                            onClick={() => {
                                                setIsCancelModalOpen(true);
                                                setIsDropdownOpen(false);
                                            }}
                                            disabled={isLoading || !credit_operation_id || isFormDisabled}
                                        >
                                            <FaBan className="mr-3 h-4 w-4 text-orange-500" />
                                            Cancelar Contrato
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            >
                <div className="container mx-auto px-2 py-4">
                    <h3 className="text-lg font-semibold mb-4">Dados da Operação</h3>
                    <div className="mb-6">
                        <div className="text-lg font-medium">
                            <span className="text-gray-600">Tipo de Operação:</span> Empréstimo CPRF
                        </div>
                    </div>

                    {/* Número do Contrato e Código de Operação */}
                    <div className="grid grid-cols-1 md:grid-cols-10 gap-4 mb-6">
                        <div className="md:col-span-2">
                            <TextEdit
                                id="sequencial_number"
                                label="Número do Contrato"
                                value={formData.sequencial_number.toString()}
                                onChange={() => { }}
                                placeholder=""
                                disabled
                            />
                        </div>
                        <div className="md:col-span-2">
                            <TextEdit
                                id="operation_number"
                                label="Código de Operação"
                                value={formData.operation_number}
                                onChange={(e) => setFormData({ ...formData, operation_number: e.target.value })}
                                maxLength={8}
                                placeholder=""
                            />
                        </div>
                        <div className="md:col-span-2">
                            <TextEdit
                                id="external_link"
                                label="Card JIRA"
                                value={formData.external_link || ''}
                                onChange={(e) => setFormData({ ...formData, external_link: e.target.value })}
                                placeholder=""
                                disabled={isFormDisabled}
                            />
                        </div>
                        <div className="md:col-span-2">
                            <SelectTextField
                                id="operation_uf"
                                label="UF da Operação"
                                value={formData.operation_uf}
                                items={provinces.map(p => ({ value: p.initials, label: p.label }))}
                                onChange={(e) => handleProvinceChange(e.target.value)}
                            />
                        </div>
                        <div className="md:col-span-2">
                            <SelectTextField
                                id="operation_city"
                                label="Cidade da Operação"
                                value={formData.operation_city}
                                items={cities}
                                onChange={(e) => setFormData({ ...formData, operation_city: e.target.value })}
                            />
                        </div>
                    </div>

                    {/* Participantes */}
                    <ParticipantsManager
                        participants={selectedParticipants}
                        onAdd={(participant) => setSelectedParticipants([...selectedParticipants, participant])}
                        onRemove={(id) => setSelectedParticipants(selectedParticipants.filter(p => p.id !== id))}
                        participantTypes={participantTypes}
                        disabled={isFormDisabled}
                    />

                    {/* Valores */}
                    <h4 className="text-md font-semibold mb-4">Valores</h4>
                    <div className="grid grid-cols-1 md:grid-cols-10 gap-4 mb-6">
                        <div className="md:col-span-2">
                            <CurrencyInput
                                id="nominal_value"
                                label="Valor Nominal *"
                                prefix="R$"
                                fractionDigits={2}
                                value={formData.nominal_value || 0}
                                onChange={(value) => setFormData({ ...formData, nominal_value: value })}
                            />
                        </div>
                        <div className="md:col-span-2">
                            <CurrencyInput
                                id="loan_amount"
                                label="Valor Financiado"
                                prefix="R$"
                                fractionDigits={2}
                                value={formData.loan_amount || 0}
                                onChange={(value) => setFormData({ ...formData, loan_amount: value })}
                            />
                        </div>
                        <div className="md:col-span-2">
                            <CurrencyInput
                                id="approved_value"
                                label="Valor Aprovado"
                                prefix="R$"
                                fractionDigits={2}
                                value={parseFloat(formData.approved_value) || 0}
                                onChange={(value) => setFormData({ ...formData, approved_value: value.toString() })}
                            />
                        </div>
                        <div className="md:col-span-2">
                            <CurrencyInput
                                id="operation_dictionary_requested_value"
                                label="Valor Solicitado"
                                prefix="R$"
                                fractionDigits={2}
                                value={formData.operation_dictionary.requested_value || 0}
                                onChange={(value) => setFormData({
                                    ...formData,
                                    operation_dictionary: {
                                        ...formData.operation_dictionary,
                                        requested_value: value
                                    }
                                })}
                            />
                        </div>
                        <div className="md:col-span-2">
                            <CurrencyInput
                                id="operation_dictionary_interest_rate"
                                label="Taxa de remuneração"
                                prefix="%"
                                fractionDigits={2}
                                value={formData.operation_dictionary.interest_rate || 0}
                                onChange={(value) => setFormData({
                                    ...formData,
                                    interest_rate: value.toString(),
                                    operation_dictionary: {
                                        ...formData.operation_dictionary,
                                        interest_rate: value
                                    }
                                })}
                            />
                        </div>
                        <div className="md:col-span-2">
                            <TextEdit
                                id="expiration_date"
                                label="Data de vencimento"
                                value={formData.expiration_date}
                                onChange={(e) => setFormData({
                                    ...formData,
                                    expiration_date: applyDateMask(e.target.value)
                                })}

                            />
                        </div>
                    </div>

                    {/* Cultura */}
                    <h4 className="text-md font-semibold mb-4">Cultura</h4>
                    <div className="grid grid-cols-1 md:grid-cols-10 gap-4 mb-6">
                        <div className="md:col-span-6">
                            <SelectTextField
                                id="operation_dictionary_crop_type_id"
                                label="Tipo de Cultura *"
                                value={formData.operation_dictionary.crop_type.id.toString()}
                                items={cropTypes}
                                prefix={<FaLeaf className="text-xl" />}
                                onChange={(e) => {
                                    const selectedCrop = cropTypes.find(crop => crop.value === e.target.value);
                                    setFormData({
                                        ...formData,
                                        operation_dictionary: {
                                            ...formData.operation_dictionary,
                                            crop_type: {
                                                id: parseInt(e.target.value, 10),
                                                name: selectedCrop?.label || ''
                                            }
                                        }
                                    });
                                    setCropTypeId(parseInt(e.target.value, 10));
                                }}
                            />
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-10 gap-4 mb-6">
                        <div className="md:col-span-2">
                            <TextEdit
                                id="operation_dictionary_commodity_harvest_year_reference"
                                label="Ano de Safra *"
                                value={formData.operation_dictionary.commodity.harvest_year_reference}
                                onChange={handleHarvestYearChange}
                                placeholder=""
                                maxLength={5}
                            />
                        </div>
                        <div className="md:col-span-2">
                            <CurrencyInput
                                id="operation_dictionary_commodity_price"
                                label="Valor da cotação *"
                                prefix="R$"
                                fractionDigits={2}
                                value={formData.operation_dictionary.commodity.price || 0}
                                onChange={(value) => setFormData({
                                    ...formData,
                                    operation_dictionary: {
                                        ...formData.operation_dictionary,
                                        commodity: { ...formData.operation_dictionary.commodity, price: value }
                                    }
                                })}
                            />
                        </div>
                        <div className="md:col-span-2">
                            <TextEdit
                                id="quotation_date"
                                label="Data da Cotação *"
                                value={formData.operation_dictionary.commodity.quotation.date}
                                onChange={(e) => setFormData({
                                    ...formData,
                                    operation_dictionary: {
                                        ...formData.operation_dictionary,
                                        commodity: {
                                            ...formData.operation_dictionary.commodity,
                                            quotation: { ...formData.operation_dictionary.commodity.quotation, date: applyDateMask(e.target.value) }
                                        }
                                    }
                                })}

                            />
                        </div>
                        <div className="md:col-span-4">
                            <TextEdit
                                id="operation_dictionary_commodity_quotation_source"
                                label="Fonte da Cotação"
                                value={formData.operation_dictionary.commodity.quotation.source}
                                onChange={(e) => setFormData({
                                    ...formData,
                                    operation_dictionary: {
                                        ...formData.operation_dictionary,
                                        commodity: {
                                            ...formData.operation_dictionary.commodity,
                                            quotation: { ...formData.operation_dictionary.commodity.quotation, source: e.target.value }
                                        }
                                    }
                                })}
                            />
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                        <SelectTextField
                            id="operation_dictionary_commodity_quotation_province_initials"
                            label="UF origem da cotação"
                            emptyLabel="..."
                            prefix={<FaMap className="text-xl" />}
                            value={formData.operation_dictionary.commodity.quotation.province_initials}
                            items={provinces.map(p => ({ value: p.initials, label: p.label }))}
                            onChange={(e) => {
                                const selectedProvince = provinces.find(p => p.initials === e.target.value);
                                if (selectedProvince) {
                                    handleQuotationProvinceChange(selectedProvince.value);
                                }
                            }}
                        />
                        <SelectTextField
                            id="operation_dictionary_commodity_quotation_city"
                            label="Cidade origem da cotação"
                            emptyLabel="..."
                            prefix={<FaMapLocationDot className="text-xl" />}
                            value={formData.operation_dictionary.commodity.quotation.city}
                            items={cities.map(c => ({ value: c.label, label: c.label }))}
                            isLoading={isLoading}
                            onChange={(e) => {
                                setFormData({
                                    ...formData,
                                    operation_dictionary: {
                                        ...formData.operation_dictionary,
                                        commodity: {
                                            ...formData.operation_dictionary.commodity,
                                            quotation: { ...formData.operation_dictionary.commodity.quotation, city: e.target.value }
                                        }
                                    }
                                });
                            }}
                        />

                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                        <CurrencyInput
                            id="operation_dictionary_measures_amount_in_aggregation_unit"
                            label={`Quantidade em ${selectCropType?.aggregationUnitMeasureDTO?.plural || ''}`}
                            fractionDigits={3}
                            value={formData.operation_dictionary.measures.amount_in_aggregation_unit || 0}
                            onChange={(value) => setFormData({
                                ...formData,
                                operation_dictionary: {
                                    ...formData.operation_dictionary,
                                    measures: { ...formData.operation_dictionary.measures, amount_in_aggregation_unit: value }
                                }
                            })}
                        />
                        <CurrencyInput
                            id="operation_dictionary_measures_referency"
                            label={`Quantidade de ${selectCropType?.commodityUnitMeasure || ''} por ${selectCropType?.aggregationUnitMeasureDTO?.singular || ''}`}
                            fractionDigits={0}
                            value={formData.operation_dictionary.measures.referency || 0}
                            onChange={(value) => setFormData({
                                ...formData,
                                operation_dictionary: {
                                    ...formData.operation_dictionary,
                                    measures: { ...formData.operation_dictionary.measures, referency: value }
                                }
                            })}
                        />
                        <TextEdit
                            id="operation_dictionary_measures_unit_measure"
                            label={`Unidade de Medida`}
                            value={formData.operation_dictionary.measures.unit_measure || ''}
                            onChange={(e) => setFormData({
                                ...formData,
                                operation_dictionary: {
                                    ...formData.operation_dictionary,
                                    measures: { ...formData.operation_dictionary.measures, unit_measure: e.target.value }
                                }
                            })}
                        />
                    </div>

                    <div className="mb-6">
                        <TextArea
                            id="commodity-description"
                            rows={4}
                            label="Descrição do produto"
                            value={formData.operation_dictionary.commodity.description}

                            onChange={(e) => setFormData({
                                ...formData,
                                operation_dictionary: {
                                    ...formData.operation_dictionary,
                                    commodity: { ...formData.operation_dictionary.commodity, description: e.target.value }
                                }
                            })}
                        />
                    </div>

                    <div className="mb-6">
                        <TextArea
                            label="Qualidade"
                            value={formData.operation_dictionary.commodity.quality}
                            onChange={(e) => setFormData({
                                ...formData,
                                operation_dictionary: {
                                    ...formData.operation_dictionary,
                                    commodity: { ...formData.operation_dictionary.commodity, quality: e.target.value }
                                }
                            })}
                        />
                    </div>

                    {/* Guarantors Section */}
                    <GuarantorsManager
                        guarantors={{
                            items: (formData.operation_dictionary.guarantors || []).map(g => {
                                console.log('🔍 Mapeando guarantor:', g);
                                return {
                                    uuid: g.uuid,
                                    name: g.name,
                                    document: g.document || '',
                                    entity_type: g.entity_type || '',
                                    guarantee_percentage: g.guarantee_percentage,
                                    participant_type_name: g.participant_type_name
                                };
                            }),
                            quantity: (formData.operation_dictionary.guarantors || []).length
                        }}
                        guaranteeValue={formData.operation_dictionary.guarantee_value || null}
                        onGuaranteeValueChange={(value) => {
                            setFormData({
                                ...formData,
                                operation_dictionary: {
                                    ...formData.operation_dictionary,
                                    guarantee_value: value
                                }
                            });
                        }}
                        onAdd={(guarantor) => {
                            const currentGuarantors = formData.operation_dictionary.guarantors || [];
                            setFormData({
                                ...formData,
                                operation_dictionary: {
                                    ...formData.operation_dictionary,
                                    guarantors: [...currentGuarantors, {
                                        uuid: guarantor.uuid,
                                        name: guarantor.name,
                                        document: guarantor.document,
                                        entity_type: guarantor.entity_type,
                                        guarantee_percentage: guarantor.guarantee_percentage || 0,
                                        participant_type_name: guarantor.participant_type_name
                                    }]
                                }
                            });
                        }}
                        onRemove={(index) => {
                            const currentGuarantors = formData.operation_dictionary.guarantors || [];
                            setFormData({
                                ...formData,
                                operation_dictionary: {
                                    ...formData.operation_dictionary,
                                    guarantors: currentGuarantors.filter((_, i) => i !== index)
                                }
                            });
                        }}
                        disabled={isFormDisabled}
                    />

                    {/* Main Risk Section */}
                    <div className="grid grid-cols-12 gap-4 mt-4">
                        <div className="col-span-6">
                            <SelectTextField
                                id="main_risk_uuid"
                                label="Risco Principal"
                                value={formData.operation_dictionary.main_risk?.uuid || ''}
                                items={[
                                    ...(selectedParticipants
                                        .filter(p => p.participant_type === 'BORROWER')
                                        .map(p => ({ value: p.person_id, label: p.name }))),
                                    ...(formData.operation_dictionary.guarantors || []).map(g => ({ value: g.uuid, label: g.name }))
                                ]}
                                onChange={(e) => {
                                    const selectedBorrower = selectedParticipants.find(p => p.person_id === e.target.value);
                                    const selectedGuarantor = formData.operation_dictionary.guarantors?.find(g => g.uuid === e.target.value);

                                    setFormData({
                                        ...formData,
                                        operation_dictionary: {
                                            ...formData.operation_dictionary,
                                            main_risk: {
                                                uuid: e.target.value,
                                                name: selectedBorrower?.name || selectedGuarantor?.name || ''
                                            }
                                        }
                                    });
                                }}
                                disabled={isFormDisabled}
                            />
                        </div>
                    </div>

                    {/* Fees Section */}
                    <FeesManager
                        fees={{
                            items: formData.operation_dictionary.fees?.items || [],
                            quantity: formData.operation_dictionary.fees?.items.length || 0
                        }}
                        onAdd={(fee) => {
                            const currentFees = formData.operation_dictionary.fees?.items || [];
                            setFormData({
                                ...formData,
                                operation_dictionary: {
                                    ...formData.operation_dictionary,
                                    fees: {
                                        items: [...currentFees, fee],
                                        quantity: currentFees.length + 1
                                    }
                                }
                            });
                        }}
                        onUpdate={(index, fee) => {
                            const currentFees = formData.operation_dictionary.fees?.items || [];
                            const updatedFees = currentFees.map((f, i) => i === index ? fee : f);
                            setFormData({
                                ...formData,
                                operation_dictionary: {
                                    ...formData.operation_dictionary,
                                    fees: {
                                        items: updatedFees,
                                        quantity: updatedFees.length
                                    }
                                }
                            });
                        }}
                        onRemove={(index) => {
                            const currentFees = formData.operation_dictionary.fees?.items || [];
                            const updatedFees = currentFees.filter((_, i) => i !== index);
                            setFormData({
                                ...formData,
                                operation_dictionary: {
                                    ...formData.operation_dictionary,
                                    fees: {
                                        items: updatedFees,
                                        quantity: updatedFees.length
                                    }
                                }
                            });
                        }}
                        disabled={isFormDisabled}
                    />

                    {/* Disbursement Section */}
                    <DisbursementLoanManager disbursements={disbursements} />

                    {/* Installments Section */}
                    <InstallmentManager
                        installments={formData.operation_dictionary.installment?.items || []}
                        nominalValue={formData.nominal_value}
                        onAdd={(installment) => {
                            const currentInstallments = formData.operation_dictionary.installment?.items || [];
                            const newInstallments = [...currentInstallments, installment];
                            const total = newInstallments.reduce((sum, item) => sum + item.value, 0);
                            const lastDueDate = newInstallments.length > 0
                                ? newInstallments[newInstallments.length - 1].dueDate
                                : '';

                            setFormData({
                                ...formData,
                                operation_dictionary: {
                                    ...formData.operation_dictionary,
                                    installment: {
                                        total,
                                        lastDueDate,
                                        quantity: newInstallments.length,
                                        items: newInstallments
                                    }
                                }
                            });
                        }}
                        onUpdateItem={(index, installment) => {
                            const currentInstallments = formData.operation_dictionary.installment?.items || [];
                            const updatedInstallments = currentInstallments.map((inst, i) => i === index ? installment : inst);
                            const total = updatedInstallments.reduce((sum, item) => sum + item.value, 0);
                            const lastDueDate = updatedInstallments.length > 0
                                ? updatedInstallments[updatedInstallments.length - 1].dueDate
                                : '';

                            setFormData({
                                ...formData,
                                operation_dictionary: {
                                    ...formData.operation_dictionary,
                                    installment: {
                                        total,
                                        lastDueDate,
                                        quantity: updatedInstallments.length,
                                        items: updatedInstallments
                                    }
                                }
                            });
                        }}
                        onRemove={(index) => {
                            const currentInstallments = formData.operation_dictionary.installment?.items || [];
                            const updatedInstallments = currentInstallments.filter((_, i) => i !== index);
                            const total = updatedInstallments.reduce((sum, item) => sum + item.value, 0);
                            const lastDueDate = updatedInstallments.length > 0
                                ? updatedInstallments[updatedInstallments.length - 1].dueDate
                                : '';

                            setFormData({
                                ...formData,
                                operation_dictionary: {
                                    ...formData.operation_dictionary,
                                    installment: {
                                        total,
                                        lastDueDate,
                                        quantity: updatedInstallments.length,
                                        items: updatedInstallments
                                    }
                                }
                            });
                        }}
                        onUpdate={(updatedInstallments, lastDueDate) => {
                            const total = updatedInstallments.reduce((sum, item) => sum + item.value, 0);

                            setFormData({
                                ...formData,
                                expiration_date: lastDueDate || formData.expiration_date,
                                operation_dictionary: {
                                    ...formData.operation_dictionary,
                                    installment: {
                                        total,
                                        lastDueDate: lastDueDate || '',
                                        quantity: updatedInstallments.length,
                                        items: updatedInstallments
                                    }
                                }
                            });
                        }}
                        disabled={isFormDisabled}
                    />

                </div>
            </PageForm>
            <ConfirmationModal
                isOpen={isCancelModalOpen}
                onCancel={() => setIsCancelModalOpen(false)}
                onConfirm={handleCancelContract}
                id="cancel-contract-modal"
                title="Cancelar Contrato"
                text="Tem certeza que deseja cancelar este contrato?"
            />
        </Fragment>);
};

export default OperationLoanFormPage;